(() => {
  const WIDTH = 560;
  const HEIGHT = 720;
  const FLOOR = 704;
  const WALL = 8;
  const DANGER_LINE = 142;
  const DROP_Y = 82;
  const GRAVITY = 980;
  const RADII = [16, 21, 27, 35, 44, 54, 65, 78, 93, 111, 134];
  const VALUES = [1, 3, 6, 10, 15, 21, 28, 36, 45, 55, 66];
  const FALLBACKS = [
    { title: 'Mini 01', family: 'mini', color: '#dfa52c' },
    { title: 'Mini 02', family: 'mini', color: '#c96343' },
    { title: 'Mini 03', family: 'mini', color: '#a64b59' },
    { title: 'Mini 04', family: 'mini', color: '#9279bd' },
    { title: 'Midi 01', family: 'midi', color: '#92a8d1' },
    { title: 'Midi 02', family: 'midi', color: '#687447' },
    { title: 'Midi 03', family: 'midi', color: '#dfa52c' },
    { title: 'Midi 04', family: 'midi', color: '#c96343' },
    { title: 'Maxi 01', family: 'maxi', color: '#a64b59' },
    { title: 'Maxi 02', family: 'maxi', color: '#9279bd' },
    { title: 'Maxi final', family: 'maxi', color: '#687447' }
  ];

  const clamp = (value, min, max) => Math.max(min, Math.min(max, value));
  const randomDropLevel = () => Math.floor(Math.random() * 4);

  class NotebookDrop {
    constructor(root) {
      this.root = root;
      this.canvas = root.querySelector('[data-game-canvas]');
      this.context = this.canvas.getContext('2d');
      this.nextCanvas = root.querySelector('[data-game-next]');
      this.nextContext = this.nextCanvas.getContext('2d');
      this.scoreElement = root.querySelector('[data-game-score]');
      this.bestElement = root.querySelector('[data-game-best]');
      this.finalScoreElement = root.querySelector('[data-game-final-score]');
      this.nextNameElement = root.querySelector('[data-game-next-name]');
      this.startOverlay = root.querySelector('[data-game-start]');
      this.endOverlay = root.querySelector('[data-game-over]');
      this.liveElement = root.querySelector('[data-game-live]');
      this.catalog = this.readCatalog();
      this.configureCanvas(this.canvas, this.context, WIDTH, HEIGHT);
      this.configureCanvas(this.nextCanvas, this.nextContext, 112, 112);
      this.loadImages();
      this.best = this.loadBest();
      this.bestElement.textContent = this.best;
      this.bodies = [];
      this.particles = [];
      this.score = 0;
      this.running = false;
      this.finished = false;
      this.canDrop = false;
      this.aimX = WIDTH / 2;
      this.currentLevel = randomDropLevel();
      this.nextLevel = randomDropLevel();
      this.nextBodyId = 1;
      this.dangerSince = 0;
      this.lastTime = performance.now();
      this.accumulator = 0;
      this.frame = 0;
      this.dropTimer = 0;
      this.bindEvents();
      this.updateNext();
      this.draw();
      this.loop = this.loop.bind(this);
      this.frame = requestAnimationFrame(this.loop);
    }

    readCatalog() {
      const node = this.root.querySelector('[data-game-catalog]');
      let custom = [];
      try {
        custom = JSON.parse(node.textContent);
      } catch (error) {
        custom = [];
      }
      return FALLBACKS.map((fallback, index) => ({
        ...fallback,
        ...(custom[index] || {}),
        imageElement: null,
        imageReady: false
      }));
    }

    configureCanvas(canvas, context, width, height) {
      const ratio = Math.min(window.devicePixelRatio || 1, 2);
      canvas.width = Math.round(width * ratio);
      canvas.height = Math.round(height * ratio);
      context.setTransform(ratio, 0, 0, ratio, 0, 0);
    }

    loadImages() {
      this.catalog.forEach((entry) => {
        if (!entry.image) return;
        const image = new Image();
        image.crossOrigin = 'anonymous';
        image.onload = () => {
          entry.imageReady = true;
          this.draw();
          this.drawNext();
        };
        image.src = entry.image;
        entry.imageElement = image;
      });
    }

    loadBest() {
      try {
        return Number.parseInt(localStorage.getItem('its-notebook-drop-best'), 10) || 0;
      } catch (error) {
        return 0;
      }
    }

    saveBest() {
      if (this.score <= this.best) return;
      this.best = this.score;
      this.bestElement.textContent = this.best;
      try {
        localStorage.setItem('its-notebook-drop-best', String(this.best));
      } catch (error) {
        return;
      }
    }

    bindEvents() {
      this.root.querySelector('[data-game-start-button]').addEventListener('click', () => this.start());
      this.root.querySelector('[data-game-play-again]').addEventListener('click', () => this.start());
      this.root.querySelector('[data-game-restart]').addEventListener('click', () => this.start());
      this.root.querySelector('[data-game-left]').addEventListener('click', () => this.moveAim(-38));
      this.root.querySelector('[data-game-right]').addEventListener('click', () => this.moveAim(38));
      this.root.querySelector('[data-game-drop]').addEventListener('click', () => this.drop());
      this.canvas.addEventListener('pointermove', (event) => this.setAimFromPointer(event));
      this.canvas.addEventListener('pointerdown', (event) => {
        event.preventDefault();
        this.canvas.focus({ preventScroll: true });
        this.setAimFromPointer(event);
        this.drop();
      });
      this.canvas.addEventListener('keydown', (event) => {
        if (event.key === 'ArrowLeft') {
          event.preventDefault();
          this.moveAim(-28);
        }
        if (event.key === 'ArrowRight') {
          event.preventDefault();
          this.moveAim(28);
        }
        if (event.key === ' ' || event.key === 'Enter' || event.key === 'ArrowDown') {
          event.preventDefault();
          this.drop();
        }
      });
      this.onSectionUnload = (event) => {
        if (event.target.contains(this.root)) this.destroy();
      };
      document.addEventListener('shopify:section:unload', this.onSectionUnload);
    }

    start() {
      clearTimeout(this.dropTimer);
      this.bodies = [];
      this.particles = [];
      this.score = 0;
      this.scoreElement.textContent = '0';
      this.currentLevel = randomDropLevel();
      this.nextLevel = randomDropLevel();
      this.aimX = WIDTH / 2;
      this.dangerSince = 0;
      this.finished = false;
      this.running = true;
      this.canDrop = true;
      this.startOverlay.hidden = true;
      this.endOverlay.hidden = true;
      this.updateNext();
      this.canvas.focus({ preventScroll: true });
      this.announce('Jogo iniciado. Solte o primeiro caderno.');
    }

    finish() {
      this.finished = true;
      this.running = false;
      this.canDrop = false;
      this.saveBest();
      this.finalScoreElement.textContent = this.score;
      this.endOverlay.hidden = false;
      this.announce(`Fim de jogo. Você fez ${this.score} pontos.`);
    }

    moveAim(distance) {
      if (!this.running) return;
      const radius = RADII[this.currentLevel];
      this.aimX = clamp(this.aimX + distance, WALL + radius, WIDTH - WALL - radius);
    }

    setAimFromPointer(event) {
      if (!this.running) return;
      const bounds = this.canvas.getBoundingClientRect();
      const radius = RADII[this.currentLevel];
      const position = ((event.clientX - bounds.left) / bounds.width) * WIDTH;
      this.aimX = clamp(position, WALL + radius, WIDTH - WALL - radius);
    }

    drop() {
      if (!this.running || !this.canDrop) return;
      const radius = RADII[this.currentLevel];
      this.bodies.push(this.createBody(this.currentLevel, clamp(this.aimX, WALL + radius, WIDTH - WALL - radius), DROP_Y));
      this.canDrop = false;
      this.currentLevel = this.nextLevel;
      this.nextLevel = randomDropLevel();
      this.updateNext();
      this.dropTimer = window.setTimeout(() => {
        if (!this.running) return;
        const nextRadius = RADII[this.currentLevel];
        this.aimX = clamp(this.aimX, WALL + nextRadius, WIDTH - WALL - nextRadius);
        this.canDrop = true;
      }, 520);
    }

    createBody(level, x, y, velocity = {}) {
      return {
        id: this.nextBodyId++,
        level,
        radius: RADII[level],
        x,
        y,
        vx: velocity.x || 0,
        vy: velocity.y || 0,
        angle: (Math.random() - 0.5) * 0.1,
        angularVelocity: (Math.random() - 0.5) * 0.7,
        age: 0
      };
    }

    updateNext() {
      const entry = this.catalog[this.nextLevel];
      this.nextNameElement.textContent = entry.title;
      this.drawNext();
    }

    drawNext() {
      const context = this.nextContext;
      context.clearRect(0, 0, 112, 112);
      context.save();
      context.translate(56, 56);
      this.drawNotebook(context, this.nextLevel, 46, 0, false);
      context.restore();
    }

    loop(time) {
      const elapsed = Math.min((time - this.lastTime) / 1000, 0.05);
      this.lastTime = time;
      if (this.running) {
        this.accumulator += elapsed;
        let steps = 0;
        while (this.accumulator >= 1 / 60 && steps < 4) {
          this.update(1 / 60, time);
          this.accumulator -= 1 / 60;
          steps += 1;
        }
      }
      this.draw();
      this.frame = requestAnimationFrame(this.loop);
    }

    update(delta, time) {
      this.bodies.forEach((body) => {
        body.age += delta;
        body.vy += GRAVITY * delta;
        body.x += body.vx * delta;
        body.y += body.vy * delta;
        body.angle += body.angularVelocity * delta;
        this.resolveBounds(body);
      });

      const merges = [];
      const reserved = new Set();
      for (let firstIndex = 0; firstIndex < this.bodies.length; firstIndex += 1) {
        for (let secondIndex = firstIndex + 1; secondIndex < this.bodies.length; secondIndex += 1) {
          const first = this.bodies[firstIndex];
          const second = this.bodies[secondIndex];
          const dx = second.x - first.x;
          const dy = second.y - first.y;
          const minimum = first.radius + second.radius;
          const distanceSquared = dx * dx + dy * dy;
          if (distanceSquared >= minimum * minimum) continue;
          if (first.level === second.level && first.level < this.catalog.length - 1 && !reserved.has(first.id) && !reserved.has(second.id)) {
            reserved.add(first.id);
            reserved.add(second.id);
            merges.push([first, second]);
            continue;
          }
          this.resolvePair(first, second, dx, dy, Math.sqrt(distanceSquared) || 0.001, minimum);
        }
      }

      merges.forEach((pair) => this.merge(pair[0], pair[1]));
      this.updateParticles(delta);
      this.checkDanger(time);
    }

    resolveBounds(body) {
      const minimumX = WALL + body.radius;
      const maximumX = WIDTH - WALL - body.radius;
      if (body.x < minimumX) {
        body.x = minimumX;
        body.vx = Math.abs(body.vx) * 0.22;
        body.angularVelocity *= 0.82;
      }
      if (body.x > maximumX) {
        body.x = maximumX;
        body.vx = -Math.abs(body.vx) * 0.22;
        body.angularVelocity *= 0.82;
      }
      const maximumY = FLOOR - body.radius;
      if (body.y > maximumY) {
        body.y = maximumY;
        if (body.vy > 0) body.vy *= -0.16;
        if (Math.abs(body.vy) < 10) body.vy = 0;
        body.vx *= 0.91;
        body.angularVelocity *= 0.9;
      }
    }

    resolvePair(first, second, dx, dy, distance, minimum) {
      const normalX = dx / distance;
      const normalY = dy / distance;
      const firstMass = first.radius * first.radius;
      const secondMass = second.radius * second.radius;
      const totalMass = firstMass + secondMass;
      const overlap = minimum - distance;
      first.x -= normalX * overlap * (secondMass / totalMass);
      first.y -= normalY * overlap * (secondMass / totalMass);
      second.x += normalX * overlap * (firstMass / totalMass);
      second.y += normalY * overlap * (firstMass / totalMass);
      const relativeX = second.vx - first.vx;
      const relativeY = second.vy - first.vy;
      const normalSpeed = relativeX * normalX + relativeY * normalY;
      if (normalSpeed >= 0) return;
      const impulse = (-(1.12) * normalSpeed) / (1 / firstMass + 1 / secondMass);
      const impulseX = impulse * normalX;
      const impulseY = impulse * normalY;
      first.vx -= impulseX / firstMass;
      first.vy -= impulseY / firstMass;
      second.vx += impulseX / secondMass;
      second.vy += impulseY / secondMass;
      const tangentX = -normalY;
      const tangentY = normalX;
      const tangentSpeed = relativeX * tangentX + relativeY * tangentY;
      const frictionImpulse = clamp(-tangentSpeed / (1 / firstMass + 1 / secondMass), -impulse * 0.18, impulse * 0.18);
      first.vx -= (frictionImpulse * tangentX) / firstMass;
      first.vy -= (frictionImpulse * tangentY) / firstMass;
      second.vx += (frictionImpulse * tangentX) / secondMass;
      second.vy += (frictionImpulse * tangentY) / secondMass;
      first.angularVelocity -= tangentSpeed * 0.0014;
      second.angularVelocity += tangentSpeed * 0.0014;
    }

    merge(first, second) {
      if (!this.bodies.some((body) => body.id === first.id) || !this.bodies.some((body) => body.id === second.id)) return;
      const nextLevel = first.level + 1;
      const x = (first.x + second.x) / 2;
      const y = (first.y + second.y) / 2;
      const velocity = {
        x: (first.vx + second.vx) / 2,
        y: Math.min((first.vy + second.vy) / 2, -40)
      };
      this.bodies = this.bodies.filter((body) => body.id !== first.id && body.id !== second.id);
      this.bodies.push(this.createBody(nextLevel, x, y, velocity));
      this.score += VALUES[nextLevel];
      this.scoreElement.textContent = this.score;
      this.saveBest();
      this.addParticles(x, y, this.catalog[nextLevel].color, 8 + nextLevel);
      this.announce(`${this.catalog[first.level].title} virou ${this.catalog[nextLevel].title}.`);
    }

    addParticles(x, y, color, amount) {
      for (let index = 0; index < amount; index += 1) {
        const angle = Math.random() * Math.PI * 2;
        const speed = 55 + Math.random() * 120;
        this.particles.push({
          x,
          y,
          vx: Math.cos(angle) * speed,
          vy: Math.sin(angle) * speed - 25,
          rotation: Math.random() * Math.PI,
          angularVelocity: (Math.random() - 0.5) * 8,
          life: 0.55 + Math.random() * 0.35,
          maximumLife: 0.9,
          color
        });
      }
    }

    updateParticles(delta) {
      this.particles.forEach((particle) => {
        particle.life -= delta;
        particle.vy += 350 * delta;
        particle.x += particle.vx * delta;
        particle.y += particle.vy * delta;
        particle.rotation += particle.angularVelocity * delta;
      });
      this.particles = this.particles.filter((particle) => particle.life > 0);
    }

    checkDanger(time) {
      const danger = this.bodies.some((body) => body.age > 1.25 && body.y - body.radius < DANGER_LINE && Math.abs(body.vy) < 48);
      if (!danger) {
        this.dangerSince = 0;
        return;
      }
      if (!this.dangerSince) this.dangerSince = time;
      if (time - this.dangerSince > 1450) this.finish();
    }

    draw() {
      const context = this.context;
      context.clearRect(0, 0, WIDTH, HEIGHT);
      this.drawBackground(context);
      this.bodies.forEach((body) => {
        context.save();
        context.translate(body.x, body.y);
        this.drawNotebook(context, body.level, body.radius, body.angle, true);
        context.restore();
      });
      this.drawParticles(context);
      if (this.running && this.canDrop) this.drawPreview(context);
    }

    drawBackground(context) {
      context.fillStyle = '#fbf7f1';
      context.fillRect(0, 0, WIDTH, HEIGHT);
      context.fillStyle = 'rgba(146, 168, 209, 0.16)';
      context.fillRect(0, 0, WIDTH, DANGER_LINE);
      context.fillStyle = 'rgba(48, 35, 31, 0.09)';
      for (let y = 18; y < HEIGHT; y += 24) {
        for (let x = 18; x < WIDTH; x += 24) {
          context.beginPath();
          context.arc(x, y, 1.15, 0, Math.PI * 2);
          context.fill();
        }
      }
      context.save();
      context.setLineDash([8, 8]);
      context.strokeStyle = '#a64b59';
      context.lineWidth = 2;
      context.beginPath();
      context.moveTo(WALL, DANGER_LINE);
      context.lineTo(WIDTH - WALL, DANGER_LINE);
      context.stroke();
      context.restore();
      context.fillStyle = '#a64b59';
      context.font = '700 10px ui-monospace, monospace';
      context.letterSpacing = '1px';
      context.fillText('LIMITE DA PILHA', 18, DANGER_LINE - 10);
      context.fillStyle = '#30231f';
      context.fillRect(0, FLOOR, WIDTH, HEIGHT - FLOOR);
    }

    drawPreview(context) {
      const radius = RADII[this.currentLevel];
      context.save();
      context.strokeStyle = 'rgba(48, 35, 31, 0.25)';
      context.setLineDash([5, 7]);
      context.beginPath();
      context.moveTo(this.aimX, DROP_Y + radius + 7);
      context.lineTo(this.aimX, DANGER_LINE - 4);
      context.stroke();
      context.globalAlpha = 0.88;
      context.translate(this.aimX, DROP_Y);
      this.drawNotebook(context, this.currentLevel, radius, 0, false);
      context.restore();
    }

    drawNotebook(context, level, radius, angle, shadow) {
      const entry = this.catalog[level];
      const family = entry.family || 'mini';
      let width = radius * 1.18;
      let height = radius * 1.48;
      if (family === 'mini') {
        width = radius * 1.02;
        height = radius * 1.68;
      }
      if (family === 'maxi') {
        width = radius * 1.44;
        height = radius * 1.2;
      }
      const corner = Math.max(2.5, radius * 0.1);
      context.rotate(angle);
      if (shadow) {
        context.save();
        context.globalAlpha = 0.18;
        context.fillStyle = '#30231f';
        context.beginPath();
        context.ellipse(3, height * 0.48, width * 0.48, radius * 0.18, 0, 0, Math.PI * 2);
        context.fill();
        context.restore();
      }
      context.fillStyle = '#e8ddcf';
      context.beginPath();
      context.roundRect(-width / 2 + radius * 0.08, -height / 2 + radius * 0.06, width, height, corner);
      context.fill();
      context.save();
      context.beginPath();
      context.roundRect(-width / 2, -height / 2, width, height, corner);
      context.clip();
      context.fillStyle = entry.color || '#a64b59';
      context.fillRect(-width / 2, -height / 2, width, height);
      if (entry.imageReady && entry.imageElement) {
        this.drawCoverImage(context, entry.imageElement, width, height);
      } else {
        this.drawCoverPattern(context, level, width, height, radius);
      }
      context.fillStyle = 'rgba(255, 255, 255, 0.14)';
      context.fillRect(-width / 2, -height / 2, width * 0.16, height);
      context.restore();
      context.strokeStyle = 'rgba(48, 35, 31, 0.42)';
      context.lineWidth = Math.max(1, radius * 0.025);
      context.beginPath();
      context.roundRect(-width / 2, -height / 2, width, height, corner);
      context.stroke();
      this.drawWire(context, width, height, radius);
      if (radius >= 36 && !entry.imageReady) this.drawNotebookLabel(context, entry, radius, width, height);
    }

    drawCoverImage(context, image, width, height) {
      const imageRatio = image.naturalWidth / image.naturalHeight;
      const targetRatio = width / height;
      let sourceWidth = image.naturalWidth;
      let sourceHeight = image.naturalHeight;
      let sourceX = 0;
      let sourceY = 0;
      if (imageRatio > targetRatio) {
        sourceWidth = image.naturalHeight * targetRatio;
        sourceX = (image.naturalWidth - sourceWidth) / 2;
      } else {
        sourceHeight = image.naturalWidth / targetRatio;
        sourceY = (image.naturalHeight - sourceHeight) / 2;
      }
      context.drawImage(image, sourceX, sourceY, sourceWidth, sourceHeight, -width / 2, -height / 2, width, height);
    }

    drawCoverPattern(context, level, width, height, radius) {
      context.save();
      context.globalAlpha = 0.28;
      context.strokeStyle = '#f5eee5';
      context.fillStyle = '#f5eee5';
      context.lineWidth = Math.max(1, radius * 0.035);
      if (level % 4 === 0) {
        for (let x = -width; x < width; x += Math.max(7, radius * 0.28)) {
          context.beginPath();
          context.moveTo(x, height / 2);
          context.lineTo(x + height, -height / 2);
          context.stroke();
        }
      }
      if (level % 4 === 1) {
        const spacing = Math.max(7, radius * 0.3);
        for (let y = -height / 2; y < height / 2; y += spacing) {
          for (let x = -width / 2; x < width / 2; x += spacing) {
            context.beginPath();
            context.arc(x, y, Math.max(1.2, radius * 0.04), 0, Math.PI * 2);
            context.fill();
          }
        }
      }
      if (level % 4 === 2) {
        context.beginPath();
        context.arc(width * 0.22, -height * 0.2, radius * 0.42, 0, Math.PI * 2);
        context.stroke();
        context.beginPath();
        context.arc(-width * 0.18, height * 0.28, radius * 0.3, 0, Math.PI * 2);
        context.stroke();
      }
      if (level % 4 === 3) {
        for (let y = -height / 2; y <= height / 2; y += Math.max(9, radius * 0.36)) {
          context.beginPath();
          context.moveTo(-width / 2, y);
          context.bezierCurveTo(-width / 4, y - radius * 0.18, width / 4, y + radius * 0.18, width / 2, y);
          context.stroke();
        }
      }
      context.restore();
    }

    drawWire(context, width, height, radius) {
      const ringCount = clamp(Math.round(height / Math.max(8, radius * 0.26)), 3, 9);
      const startY = -height * 0.38;
      const distance = (height * 0.76) / Math.max(1, ringCount - 1);
      context.strokeStyle = '#c49a3a';
      context.lineWidth = Math.max(1.2, radius * 0.045);
      for (let index = 0; index < ringCount; index += 1) {
        const y = startY + distance * index;
        context.beginPath();
        context.ellipse(-width / 2, y, radius * 0.105, radius * 0.052, 0, 0, Math.PI * 2);
        context.stroke();
      }
    }

    drawNotebookLabel(context, entry, radius, width, height) {
      context.save();
      context.fillStyle = '#f5eee5';
      context.textAlign = 'center';
      context.textBaseline = 'middle';
      context.font = `700 ${Math.max(7, radius * 0.16)}px ui-monospace, monospace`;
      context.fillText(String(entry.family || '').toUpperCase(), width * 0.04, -height * 0.08);
      if (radius >= 58) {
        context.font = `600 ${Math.max(8, radius * 0.13)}px Georgia, serif`;
        const title = String(entry.title || '').slice(0, 15);
        context.fillText(title, width * 0.04, height * 0.1, width * 0.74);
      }
      context.restore();
    }

    drawParticles(context) {
      this.particles.forEach((particle) => {
        context.save();
        context.globalAlpha = clamp(particle.life / particle.maximumLife, 0, 1);
        context.translate(particle.x, particle.y);
        context.rotate(particle.rotation);
        context.fillStyle = particle.color;
        context.fillRect(-3, -3, 6, 6);
        context.restore();
      });
    }

    announce(message) {
      this.liveElement.textContent = '';
      window.setTimeout(() => {
        this.liveElement.textContent = message;
      }, 20);
    }

    destroy() {
      cancelAnimationFrame(this.frame);
      clearTimeout(this.dropTimer);
      document.removeEventListener('shopify:section:unload', this.onSectionUnload);
    }
  }

  const initialize = (scope = document) => {
    scope.querySelectorAll('[data-notebook-drop]').forEach((root) => {
      if (root.dataset.gameReady === 'true') return;
      root.dataset.gameReady = 'true';
      new NotebookDrop(root);
    });
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initialize());
  } else {
    initialize();
  }

  document.addEventListener('shopify:section:load', (event) => initialize(event.target));
})();
